import React, { useEffect } from "react"
import Head from "next/head"
import AemGrid from "@/components/AemGrid"
import { loveYourLookDataUrl, footerUrl, headerUrl } from "@/constants"
import {
  fetchPageModel,
  fetchPageShell,
} from "@/services/server/aemPageData.server"
import { useDispatch } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import { filterBackEndJson, getClienIP, getDataLayerObj } from "@/utils/helper"
import { handleServerSideRouteError } from "@/utils/serverSideError"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import { setGbhDataLayerCustom } from "@/store/features/genericSlice"
import usePageAuth from "@/hooks/usePageAuth"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import BreadCrumb from "@/components/Default/BreadCrumb"
/**
 * renders home page
 * @return {JSX.Element}
 */
function Home({
  tmpData: data,
  headertmp: header,
  footertmp: footer,
  ip,
  globalScript,
  footerScript,
  analyticsData,
  gbhDataLayerCustom,
}) {
  const dispatch = useDispatch()
  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])
  usePageAuth()
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])
  useEffect(() => {
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    return () => dispatch(setGbhDataLayerCustom({}))
  }, [])
  return (
    <>
      <Head>
        <title>{data?.title}</title>
        <meta name="keywords" content="Kohler US Plumbing Bathroom Kitchen" />
        <meta name="description" content={data?.description} />
        <meta property="og:title" content={data?.title} />
        <meta property="og:description" content={data?.description} />
        <meta property="og:url" content="/en" />
        <meta property="og:type" content={data?.templateName} />
      </Head>
      <>
        <GlobalPageHeader
          themeType={data?.appliedCssClassNames}
          header={header}
          title={data.title}
          globalScripting={globalScript}
          data={data}
        />
        <AemGrid
          data={data}
          count={0}
          breadCrumb={(data, fullData) => (
            <BreadCrumb data={data} fullData={fullData} />
          )}
          productAccordion={(data, fullData) => (
            <ProductAccordion data={data} fullData={fullData} />
          )}
        />
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      </>
    </>
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({ req, res, locale }) {
  try {
    await i18n?.reloadResources()
    const ip = getClienIP(req)
    const urlPath = req.url.split("?")[0] ?? ""
    const data = await fetchPageModel(loveYourLookDataUrl)
    const headerModelUrl = `${
      data.headerExpFrg ? data.headerExpFrg : headerUrl
    }.model.json`
    const footerModelUrl = `${
      data.footerExpFrg ? data.footerExpFrg : footerUrl
    }.model.json`
    const { header: header, footer: footer } = await fetchPageShell({
      headerModelUrl,
      footerModelUrl,
      pageName: "Contentpage",
      context: { locale },
    })
    const globalScript = data?.globalScript ? data?.globalScript : null
    const footerScript = data?.footerScript ? data?.footerScript : null
    const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
    const analyticsScriptClass = data?.analyticsScriptClass
      ? data?.analyticsScriptClass
      : null
    let tmpData = {}
    if (data.pageComponentProperties) {
      tmpData = data.pageComponentProperties
    } else {
      tmpData = data
    }
    const analyticsData = data?.data ? data?.data : null
    const gbhDataLayerCustom = {}
    if (data?.datalayerPageName) {
      gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
    }
    if (data?.eventPageType) {
      gbhDataLayerCustom.eventPageType = data?.eventPageType
    }
    if (data?.pageType) {
      gbhDataLayerCustom.pageType = data?.pageType
    }
    tmpData = filterBackEndJson(tmpData)
    const headertmp = filterBackEndJson(header)
    const footertmp = filterBackEndJson(footer)
    const metaContent = data?.metaContent ?? ""
    const canonicalUrl = data?.canonicalUrl ?? ""
    // Pass data to the page via props
    return {
      props: {
        ip,
        tmpData,
        headertmp,
        footertmp,
        globalScript,
        footerScript,
        topHeadScript,
        analyticsData,
        analyticsScriptClass,
        gbhDataLayerCustom,
        urlPath,
        metaContent,
        canonicalUrl,
        ...(await serverSideTranslations(locale, ["common"])),
      },
    }
  } catch (error) {
    return handleServerSideRouteError(
      "contentpage page data fetch failed",
      error
    )
  }
}
export default Home
